import { assertNever } from "@/shared/lib/assertNever";
import { safeParseInt } from "@/shared/lib/number";
import {
  MenuItem,
  Select,
  type SelectProps,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from "@mui/material";
import React, { forwardRef } from "react";
import type { Merge } from "type-fest";

export type BaseSelectProps = {
  noSelectedOptionText?: string;
  helperText?: string;
  unit?: string;
} & (
  | ({ type: "string" } & Merge<
      SelectProps<string | undefined>,
      {
        value: string | undefined;
        onChange?: (value: string | "") => void;
      }
    >)
  | ({ type: "number" } & Merge<
      SelectProps<number | undefined>,
      {
        value: number | undefined;
        onChange?: (value: number | "") => void;
      }
    >)
);

export const BaseSelect = forwardRef(
  (props: BaseSelectProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const {
      disabled,
      error,
      sx,
      noSelectedOptionText,
      helperText,
      unit,
      placeholder,
      ...restProps
    } = props;
    const mergedSx: SxProps<Theme> = {
      minWidth: 128,
      borderRadius: "12px",
      bgcolor: disabled ? "#F2F3F3" : error ? "#FEF4F2" : "white",
      color: error ? "#EA583B" : "grey.900",
      "& .MuiSelect-outlined": {
        borderRadius: "12px",
        backgroundColor: disabled ? "#F2F3F3" : error ? "#FEF4F2" : "white",
        color: error ? "#EA583B" : "grey.900",
      },
      ...sx,
    };
    const optionElements = props.noSelectedOptionText
      ? [
          <MenuItem key="no-selected-option" value="">
            {props.noSelectedOptionText}
          </MenuItem>,
          ...React.Children.toArray(props.children),
        ]
      : props.children;
    let selectElement = null;
    switch (restProps.type) {
      case "string": {
        const { value, onChange, ...typedRestProps } = restProps;
        selectElement = (
          <Select
            value={value ?? ""}
            sx={mergedSx}
            onChange={(e) => {
              if (!onChange) {
                return;
              }
              onChange(e.target.value);
            }}
            {...typedRestProps}
          >
            {optionElements}
          </Select>
        );
        break;
      }
      case "number": {
        const { value, onChange, ...typedRest } = restProps;
        selectElement = (
          <Select
            value={value ?? ""}
            sx={mergedSx}
            onChange={(e) => {
              if (!onChange) {
                return;
              }
              const numberValue = safeParseInt(e.target.value, 10);
              onChange(numberValue ?? "");
            }}
            {...typedRest}
          >
            {optionElements}
          </Select>
        );
        break;
      }
      default: {
        assertNever(restProps);
      }
    }

    return (
      <Stack ref={ref} spacing={0.5}>
        <Stack direction="row" alignItems="flex-end" spacing={1}>
          {selectElement}
          <Typography variant="caption">{props.unit}</Typography>
        </Stack>
        {props.helperText && (
          <Typography
            variant="caption"
            sx={{
              color: props.error ? "error.main" : "grey.500",
            }}
          >
            {props.helperText}
          </Typography>
        )}
      </Stack>
    );
  },
);
